import styled from 'styled-components';
import {
  KADRY_SCHEDULE_KIND_LABELS,
  KADRY_SOURCE_LABELS,
  kadryPayRange,
  kadryPlural,
  type KadrySearchProfile,
  type KadryWorkSite,
} from '@entities/kadry';
import { KadryMetaList } from '../ui';

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const Chip = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
  line-height: 1.5;
  overflow-wrap: anywhere;
`;

const None = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

function ChipList({ items }: { items?: string[] }) {
  if (!items || items.length === 0) return <None>не заданы</None>;
  return (
    <Chips>
      {items.map((s) => (
        <Chip key={s}>{s}</Chip>
      ))}
    </Chips>
  );
}

function scheduleText(p: KadrySearchProfile): string {
  const s = p.schedule;
  if (!s) return 'не задан';
  const parts = [KADRY_SCHEDULE_KIND_LABELS[s.type]];
  if (s.shift) parts.push(`смена ${s.shift}`);
  if (s.type === 'rotation' && s.rotation_days != null) {
    parts.push(`${s.rotation_days} ${kadryPlural(s.rotation_days, ['день', 'дня', 'дней'])}`);
  }
  return parts.join(' · ');
}

function readinessText(p: KadrySearchProfile): string {
  const r = p.readiness_required ?? {};
  const out: string[] = [];
  if (r.rotation) out.push('к вахте');
  if (r.relocation) out.push('к переезду на объект');
  if (r.business_trips) out.push('к командировкам');
  return out.length ? `нужна готовность ${out.join(', ')}` : 'не требуется';
}

interface Props {
  profile: KadrySearchProfile;
  workSites: KadryWorkSite[];
}

/** Профиль поиска только для чтения (утверждённая версия или просмотр черновика). */
export function ProfileView({ profile: p, workSites }: Props) {
  const site = p.work_site_id ? workSites.find((s) => s.id === p.work_site_id) : undefined;
  const years = p.experience?.years_in_role_min;
  return (
    <KadryMetaList>
      <dt>Должность</dt>
      <dd>{p.position || <None>не указана</None>}</dd>
      <dt>Синонимы</dt>
      <dd>
        <ChipList items={p.synonyms} />
      </dd>
      <dt>Навыки обязательные</dt>
      <dd>
        <ChipList items={p.skills?.required} />
      </dd>
      <dt>Навыки желательные</dt>
      <dd>
        <ChipList items={p.skills?.optional} />
      </dd>
      <dt>Допуски обязательные</dt>
      <dd>
        <ChipList items={p.permits?.required} />
      </dd>
      <dt>Опыт в профильной роли</dt>
      <dd>
        {years != null
          ? `от ${years.toLocaleString('ru-RU')} ${
              Number.isInteger(years) ? kadryPlural(years, ['года', 'лет', 'лет']) : 'года'
            }`
          : 'не важен'}
      </dd>
      <dt>Объект</dt>
      <dd>{p.work_site_id ? (site?.name ?? 'объект не найден') : <None>не указан</None>}</dd>
      <dt>График</dt>
      <dd>{scheduleText(p)}</dd>
      <dt>Готовность</dt>
      <dd>{readinessText(p)}</dd>
      <dt>Оплата на руки</dt>
      <dd>
        {p.pay ? `${kadryPayRange(p.pay.from, p.pay.to)} в месяц` : <None>не указана</None>}
      </dd>
      <dt>Источники</dt>
      <dd>
        {p.sources && p.sources.length > 0 ? (
          <Chips>
            {p.sources.map((s) => (
              <Chip key={s}>{KADRY_SOURCE_LABELS[s]}</Chip>
            ))}
          </Chips>
        ) : (
          <None>не выбраны</None>
        )}
      </dd>
    </KadryMetaList>
  );
}
